import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Badge,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card.js';
import {
  downloadAcademicCertificatePdf,
  getStudentTranscript,
} from 'services/academic.service';

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-UY');
};

const statusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (['APROBADO', 'APPROVED', 'REVALIDADO', 'EXONERADO', 'PROMOVIDO'].includes(s)) {
    return 'green';
  }
  if (['NO_APROBADO', 'REPROBADO', 'AUSENTE'].includes(s)) return 'red';
  return 'gray';
};

export default function EscolaridadTab({ studentId, personName }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);
  const textPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!studentId) {
        setData(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getStudentTranscript(studentId);
        if (!cancelled) setData(res?.payload || null);
      } catch (error) {
        if (!cancelled) {
          setData(null);
          toast({
            title: 'Error al cargar escolaridad',
            description: error?.message || 'No se pudo obtener el historial académico.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, toast]);

  const handleDownload = async () => {
    if (!studentId) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadAcademicCertificatePdf(studentId, 'transcript', {
        personName,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast({
        title: 'Escolaridad descargada',
        description: filename,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al descargar',
        description: 'No se pudo generar el PDF de escolaridad.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!studentId) {
    return (
      <Card p={6}>
        <Text color={textSecondary}>Este perfil no tiene estudiante asociado.</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card p={10}>
        <Flex justify="center" align="center" gap={3}>
          <Spinner />
          <Text color={textSecondary}>Cargando escolaridad...</Text>
        </Flex>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const studyPlans = data?.studyPlans || [];
  const timeline = data?.timeline || [];
  const examGrades = data?.examGrades || [];
  const courseGrades = data?.courseGrades || [];
  const rows =
    timeline.length > 0
      ? timeline
      : [
          ...examGrades.map((g) => ({
            id: `exam-${g.id}`,
            kind: g.kind || 'Examen',
            subjectName: g.subject_name,
            date: g.exam_date,
            grade: g.grade,
            status: g.status,
          })),
          ...courseGrades.map((g) => ({
            id: `course-${g.id}`,
            kind: g.kind || 'Curso',
            subjectName: g.subject_name,
            date: g.course_date || g.created_date,
            grade: g.final_grade,
            status: g.status,
          })),
        ];

  return (
    <VStack align="stretch" spacing={5}>
      <Card p={5}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
        >
          <Box>
            <Heading size="md" color={textPrimary}>
              Escolaridad
            </Heading>
            <Text color={textSecondary} mt={1} fontSize="sm">
              Historial académico del estudiante
            </Text>
          </Box>
          <Button
            colorScheme="blue"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Generando PDF"
          >
            Descargar PDF
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={6}>
          <Box>
            <Text fontSize="sm" color={textSecondary}>
              Materias del plan
            </Text>
            <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
              {summary.totalSubjects ?? 0}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color={textSecondary}>
              Aprobadas
            </Text>
            <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
              {summary.approvedSubjects ?? 0}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color={textSecondary}>
              Avance
            </Text>
            <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
              {summary.progressPercentage ?? 0}%
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color={textSecondary}>
              Promedio
            </Text>
            <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
              {summary.averageGrade ?? 0}
            </Text>
          </Box>
        </SimpleGrid>

        {studyPlans.length > 0 && (
          <Box mt={5}>
            <Text fontSize="sm" color={textSecondary} mb={2}>
              Plan(es) de estudio
            </Text>
            <Flex gap={2} flexWrap="wrap">
              {studyPlans.map((p) => (
                <Badge key={p.id} colorScheme="purple" px={2} py={1} borderRadius="md">
                  {p.study_name || 'Estudio'} — {p.plan_name || `Plan #${p.study_plan_id}`}
                </Badge>
              ))}
            </Flex>
          </Box>
        )}
      </Card>

      <Card p={5} overflowX="auto">
        <Heading size="sm" color={textPrimary} mb={4}>
          Detalle académico ({rows.length})
        </Heading>
        {rows.length === 0 ? (
          <Text color={textSecondary}>No hay registros académicos para este estudiante.</Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Tipo</Th>
                <Th borderColor={borderColor}>Materia</Th>
                <Th borderColor={borderColor}>Fecha</Th>
                <Th borderColor={borderColor} isNumeric>
                  Nota
                </Th>
                <Th borderColor={borderColor}>Estado</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td borderColor={borderColor}>{row.kind || '-'}</Td>
                  <Td borderColor={borderColor}>{row.subjectName || row.subject_name || '-'}</Td>
                  <Td borderColor={borderColor}>{formatDate(row.date || row.exam_date)}</Td>
                  <Td borderColor={borderColor} isNumeric>
                    {row.grade ?? '-'}
                  </Td>
                  <Td borderColor={borderColor}>
                    <Badge colorScheme={statusColor(row.status)}>{row.status || '-'}</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>
    </VStack>
  );
}
